import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    Box,
    Typography,
    Avatar,
    Grid,
    Button,
    Checkbox,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    InputAdornment,
    FormControlLabel,
} from '@mui/material';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import SearchIcon from '@mui/icons-material/Search';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventIcon from '@mui/icons-material/Event';
import { useWorkspaces } from 'contexts/WorkspacesContextProvider';
import { useSnackbar } from 'contexts/SnackbarContextProvider';
import { useTranslation } from "react-i18next";
import "../../../css/components/Calendar/Events/EventsDetailsModal.css";
import "../../../css/main.css";
import { DateTime } from 'luxon';

import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import ScrollAwareTooltip from 'components/Custom/ScrollAwareTooltip';
const EventDetailsModal = ({ open, onClose, selectedEvent, myUserPerms, onSave, workspaceId, userTimezone, onDelete, autoEditOnOpen = false }) => {
    const { showSnackbar } = useSnackbar();
    const { t } = useTranslation();

    const workspaceContext = useWorkspaces();
    const workspaceWorkers = workspaceContext.workspaceWorkers

    const [editableEvent, setEditableEvent] = useState({
        id: '',
        title: '',
        creator: '',
        description: '',
        collaborators: [],
        colorCode: '#3788d8',
        all_day_event: false,
        date_start: '',
        date_end: '',
        visibility: false,
    });

    const [initialState, setInitialState] = useState(null);
    const [isEditing, setIsEditing] = useState(autoEditOnOpen);
    const [searchTerm, setSearchTerm] = useState('');
    const [originalTimeStart, setOriginalTimeStart] = useState('');
    const [originalTimeEnd, setOriginalTimeEnd] = useState('');

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        if (!open) {
            setIsEditing(false);
        }
    }, [open]);

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        if (selectedEvent) {
            setOriginalTimeStart('');
            setOriginalTimeEnd('');


            // Convert start and end times to user's timezone
            const startDateTime = selectedEvent.allDay
                ? DateTime.fromISO(selectedEvent.start, { zone: 'utc' })
                : DateTime.fromISO(selectedEvent.start, { zone: userTimezone });


            const endDateTime = selectedEvent.allDay
                ? DateTime.fromISO(selectedEvent.end, { zone: 'utc' })
                : DateTime.fromISO(selectedEvent.end, { zone: userTimezone });

            // Determine the format based on all-day status
            const convertedStart = selectedEvent.allDay
                ? startDateTime.toISODate()
                : startDateTime.toFormat("yyyy-MM-dd'T'HH:mm");

            const convertedEnd = selectedEvent.allDay
                ? endDateTime.minus({ days: 1 }).toISODate()
                : endDateTime.toFormat("yyyy-MM-dd'T'HH:mm");

            const eventDetails = {
                id: selectedEvent.id || '',
                title: selectedEvent.title || '',
                creator: selectedEvent.creator || '',
                description: selectedEvent.extendedProps.description || '',
                collaborators: selectedEvent.extendedProps.collaborators || [],
                colorCode: selectedEvent.backgroundColor || '#3788d8',
                all_day_event: selectedEvent.allDay || false,
                date_start: convertedStart,
                date_end: convertedEnd,
                visibility: selectedEvent.extendedProps.visibility || false,
            };

            setEditableEvent(eventDetails);
            setInitialState(eventDetails);
        }
    }, [selectedEvent, userTimezone]);


    const handleAddCollaborator = (worker) => {
        setEditableEvent((prev) => ({
            ...prev,
            collaborators: [...prev.collaborators, worker],
        }));
    };

    const handleRemoveCollaborator = (workerId) => {
        setEditableEvent((prev) => ({
            ...prev,
            collaborators: prev.collaborators.filter((collab) => collab.id !== workerId),
        }));
    };

    const handleEdit = () => {
        setInitialState(editableEvent);
        setIsEditing(true);
    };

    const handleCancel = () => {
        setEditableEvent(initialState);
        setIsEditing(false);
    };

    /* eslint-disable no-mixed-operators */
    const handleSave = () => {
        if ((editableEvent.all_day_event && new Date(editableEvent.date_end) < new Date(editableEvent.date_start) ||
            !editableEvent.all_day_event && new Date(editableEvent.date_end) <= new Date(editableEvent.date_start))) {
            showSnackbar(t("calendar.end_date_invalid"), 'error');
            return;
        }


        if (!editableEvent.title || !editableEvent.date_start || !editableEvent.date_end || (editableEvent.collaborators.length === 0)) {
            showSnackbar(t("calendar.req_fields_err"), 'error');
            return;
        }

        onSave(editableEvent);
        setIsEditing(false);
    };

    const filteredCollaborators = editableEvent.collaborators.filter((collab) =>
        collab.full_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const availableWorkspaceWorkers = workspaceWorkers.filter(
        (worker) => !editableEvent.collaborators.some((collab) => collab.id === worker.id)
    );

    const filteredAvailableWorkers = availableWorkspaceWorkers.filter((worker) =>
        worker.full_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        if (editableEvent.all_day_event) {
            // Store original times before switching to all-day
            if (editableEvent.date_start) {
                const dtStart = DateTime.fromISO(editableEvent.date_start);
                setOriginalTimeStart(dtStart.toFormat('HH:mm'));
                setEditableEvent(prevState => ({
                    ...prevState,  // Spread the previous state to preserve other properties
                    date_start: dtStart.toISODate(), // Set the new start date (yyyy-MM-dd format)
                }));
            }
            if (editableEvent.date_end) {
                const dtEnd = DateTime.fromISO(editableEvent.date_end);
                setOriginalTimeEnd(dtEnd.toFormat('HH:mm'));
                setEditableEvent(prevState => ({
                    ...prevState,
                    date_end: dtEnd.toISODate(),
                }));

            }
        } else {
            // Restore original times when switching back
            if (originalTimeStart) {
                setEditableEvent(prevState => ({
                    ...prevState,  // Spread the previous state to preserve other properties
                    date_start: DateTime.fromISO(editableEvent.date_start).toFormat(`yyyy-MM-dd'T'${originalTimeStart}`), // Set the new start date (yyyy-MM-dd format)
                }));
            }
            if (originalTimeEnd) {
                setEditableEvent(prevState => ({
                    ...prevState,  // Spread the previous state to preserve other properties
                    date_end: DateTime.fromISO(editableEvent.date_end).toFormat(`yyyy-MM-dd'T'${originalTimeEnd}`), // Set the new start date (yyyy-MM-dd format)
                }));
            }
        }
    }, [editableEvent.all_day_event]);

    const dateStart = DateTime.fromISO(editableEvent.date_start);
    const dateEnd = DateTime.fromISO(editableEvent.date_end);

    const hasValidTime = date =>
        !isNaN(date.hour) && !isNaN(date.minute);

    const isYearValid = (date) => {
        const dateObj = DateTime.fromISO(date);

        return !isNaN(dateObj.year) && dateObj.year <= 9999 && dateObj.year >= 1000;
    };

    const startYearValidationError = !isYearValid(dateStart);
    const endYearValidationError = !isYearValid(dateEnd);

    // For non-all-day events, check that both dates have hours and minutes set
    const isSubmitDisabled =
        (dateStart >= dateEnd && !editableEvent.all_day_event) ||
        (dateStart > dateEnd && editableEvent.all_day_event) ||
        (!editableEvent.all_day_event && (!hasValidTime(dateStart) || !hasValidTime(dateEnd)));

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle
                className="event-details-main-styles"
            >
                {isEditing ? t("calendar.event_edit_title") : editableEvent.title}
            </DialogTitle>

            <DialogContent className="event-details-content-dialogue">
                <Grid container spacing={2}>
                    {isEditing && (
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label={t("calendar.event_title") + "*"}
                                value={editableEvent.title}
                                onChange={(e) => setEditableEvent({ ...editableEvent, title: e.target.value })}
                                className="event-details-textfield"
                                InputLabelProps={{ className: "event-details-text-color" }}
                                InputProps={{ className: "white-txt-color" }}
                            />
                        </Grid>
                    )}

                    <Grid item xs={12} mt={2}>
                        {isEditing ? (
                            <TextField
                                fullWidth
                                multiline
                                label={t("calendar.event_description")}
                                value={editableEvent.description}
                                onChange={(e) => setEditableEvent({ ...editableEvent, description: e.target.value })}
                                rows={4}
                                className="event-details-textfield"
                                InputLabelProps={{ className: "event-details-text-color" }}
                                InputProps={{ className: "white-txt-color" }}
                            />
                        ) : (
                            <Typography variant="body1" className="event-details-description">
                                <strong>{t("calendar.event_description")}</strong><br /> {editableEvent.description || t("calendar.event_no_description")}
                            </Typography>
                        )}
                    </Grid>

                    {isEditing && (
                        <>
                            <Grid item xs={6}>
                                <ScrollAwareTooltip
                                    title={!endYearValidationError && t("calendar.end_date_after_start_date")}
                                    disableInteractive
                                    open={isSubmitDisabled || startYearValidationError} // Only show the tooltip when the button is disabled
                                >
                                    <TextField
                                        fullWidth
                                        label={t("global.start_date") + "*"}
                                        type={editableEvent.all_day_event ? 'date' : 'datetime-local'}
                                        value={editableEvent.date_start}
                                        onChange={(e) => setEditableEvent({ ...editableEvent, date_start: e.target.value })}
                                        className="event-details-textfield"
                                        InputLabelProps={{ className: "event-details-text-color", shrink: true }}
                                        InputProps={{ className: "white-txt-color" }}
                                        inputProps={{
                                            max: "9999-12-31", // Limits the maximum date to 4-digit year
                                        }}
                                        onInput={(e) => {
                                            // Remove any year longer than 4 digits
                                            const inputDate = e.target.value;
                                            const year = inputDate.split("-")[0];
                                            if (year.length > 4) {
                                                e.target.value = inputDate.slice(0, 4) + inputDate.slice(5);
                                            }
                                        }}
                                    />
                                </ScrollAwareTooltip>
                            </Grid>
                            <Grid item xs={6}>
                                <ScrollAwareTooltip
                                    disableInteractive
                                    open={isSubmitDisabled || endYearValidationError} // Only show the tooltip when the button is disabled
                                >
                                    <TextField
                                        fullWidth
                                        label={t("calendar.event_end_date") + "*"}
                                        type={editableEvent.all_day_event ? 'date' : 'datetime-local'}
                                        value={editableEvent.date_end}
                                        onChange={(e) => setEditableEvent({ ...editableEvent, date_end: e.target.value })}
                                        className="event-details-textfield"
                                        InputLabelProps={{ className: "event-details-text-color", shrink: true }}
                                        InputProps={{ className: "white-txt-color" }}
                                        inputProps={{
                                            max: "9999-12-31", // Limits the maximum date to 4-digit year
                                        }}
                                        onInput={(e) => {
                                            // Remove any year longer than 4 digits
                                            const inputDate = e.target.value;
                                            const year = inputDate.split("-")[0];
                                            if (year.length > 4) {
                                                e.target.value = inputDate.slice(0, 4) + inputDate.slice(5);
                                            }
                                        }}
                                    />
                                </ScrollAwareTooltip>
                            </Grid>
                        </>
                    )}

                    {isEditing && (
                        <Grid item xs={12}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={editableEvent.all_day_event}
                                                onChange={(e) => setEditableEvent({ ...editableEvent, all_day_event: e.target.checked })}
                                                className="event-details-text-color"
                                            />
                                        }
                                        label={t("calendar.event_all_day")}
                                        className="event-details-text-color"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={editableEvent.visibility}
                                                onChange={(e) => setEditableEvent({ ...editableEvent, visibility: e.target.checked })}
                                                className="event-details-text-color"
                                            />
                                        }
                                        label={t("calendar.event_public")}
                                        className="event-details-text-color"
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    )}

                    {isEditing && (
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label={t("calendar.event_color")}
                                value={editableEvent.colorCode}
                                onChange={(e) => setEditableEvent({ ...editableEvent, colorCode: e.target.value })}
                                type="color"
                                className="event-details-textfield"
                                InputLabelProps={{ className: "event-details-text-color" }}
                                InputProps={{ className: "white-txt-color" }}
                            />
                        </Grid>
                    )}
                    {!isEditing && (
                        <Box className="event-details-edit">
                            <Box>
                                <Box className="event-details-edit-content">
                                    {!editableEvent.all_day_event ? (
                                        <>
                                            <EventIcon className="event-details-event-info-margin" />
                                            <Typography variant="body1">
                                                {DateTime.fromISO(editableEvent.date_start, { zone: userTimezone }).hasSame(DateTime.fromISO(editableEvent.date_end, { zone: userTimezone }), 'day')
                                                    ? DateTime.fromISO(editableEvent.date_start, { zone: userTimezone }).setLocale(t("global.short_lng")).toLocaleString(DateTime.DATE_SHORT)
                                                    : `${DateTime.fromISO(editableEvent.date_start, { zone: userTimezone }).setLocale(t("global.short_lng")).toLocaleString(DateTime.DATE_SHORT)} ${t('profile.to')} ${DateTime.fromISO(editableEvent.date_end, { zone: userTimezone }).setLocale(t("global.short_lng")).toLocaleString(DateTime.DATE_SHORT)}`
                                                }
                                            </Typography>
                                            <AccessTimeIcon className="event-details-access-info-margin" />
                                            <Typography variant="body1">
                                                {`${DateTime.fromISO(editableEvent.date_start, { zone: userTimezone }).setLocale(t("global.short_lng")).toLocaleString(DateTime.TIME_SIMPLE)} ${t('profile.to')} ${DateTime.fromISO(editableEvent.date_end, { zone: userTimezone }).setLocale(t("global.short_lng")).toLocaleString(DateTime.TIME_SIMPLE)}`}
                                            </Typography>
                                        </>
                                    ) : (
                                        <>
                                            <EventIcon className="event-details-event-info-margin" />
                                            <Typography variant="body1">
                                                {DateTime.fromISO(editableEvent.date_start, { zone: userTimezone }).hasSame(DateTime.fromISO(editableEvent.date_end, { zone: userTimezone }), 'day')
                                                    ? `${t('calendar.all_day_event')} ${DateTime.fromISO(editableEvent.date_start, { zone: userTimezone }).setLocale(t("global.short_lng")).toLocaleString(DateTime.DATE_SHORT)}`
                                                    : `${t('calendar.all_day_event2')} ${DateTime.fromISO(editableEvent.date_start, { zone: userTimezone }).setLocale(t("global.short_lng")).toLocaleString(DateTime.DATE_SHORT)} ${t('profile.to')} ${DateTime.fromISO(editableEvent.date_end, { zone: userTimezone }).setLocale(t("global.short_lng")).toLocaleString(DateTime.DATE_SHORT)}`
                                                }
                                            </Typography>
                                        </>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    )}

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder={t("calendar.event_collab_search")}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="event-details-textfield"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon className="event-details-text-color" />
                                    </InputAdornment>
                                ),
                                className: "white-txt-color",
                            }}
                            InputLabelProps={{ className: "event-details-text-color" }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Typography variant="h6" className="event-details-text-color">
                            {t("calendar.event_collabs")} {isEditing ? "*" : ''}
                        </Typography>
                        <List className="event-details-collab-list">
                            {filteredCollaborators.length > 0 ? (
                                filteredCollaborators.map((collab) => (
                                    <ListItem key={collab.id} button={isEditing} onClick={() => isEditing && handleRemoveCollaborator(collab.id)}>
                                        <ListItemAvatar>
                                            <Avatar src={collab.image} />
                                        </ListItemAvatar>
                                        <ListItemText primary={collab.full_name} className="event-details-text-color" />
                                        {isEditing && (
                                            <RemoveCircleIcon className="event-details-remove-collab" />
                                        )}
                                    </ListItem>
                                ))
                            ) : (
                                <Typography variant="body2" className="event-details-no-collabs">
                                    {t("calendar.event_no_collabs")}
                                </Typography>
                            )}
                        </List>
                    </Grid>

                    {isEditing && (
                        <Grid item xs={12} sm={6}>
                            <Typography variant="h6" className="event-details-add-collabs">
                                {t("calendar.event_add_collabs")}
                            </Typography>
                            <List className="event-details-collab-list">
                                {filteredAvailableWorkers.length > 0 ? (
                                    filteredAvailableWorkers.map((worker) => (
                                        <ListItem key={worker.id} button onClick={() => handleAddCollaborator(worker)}>
                                            <ListItemAvatar>
                                                <Avatar src={worker.image} />
                                            </ListItemAvatar>
                                            <ListItemText primary={worker.full_name} className="event-details-text-color" />
                                            <AddCircleIcon className="event-details-add-collab" />
                                        </ListItem>
                                    ))
                                ) : (
                                    <Typography variant="body2" className="event-details-no-collabs">
                                        {t("calendar.event_no_workers")}
                                    </Typography>
                                )}
                            </List>
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <Box className="buttons-flex">
                            {!isEditing && (
                                <Button onClick={onClose} className="decline-button">
                                    {t("global.close")}
                                </Button>
                            )}
                            {isEditing ? (
                                <div className='button-base-mobile'>
                                    <div className='left-side-buttons'>
                                        <Button
                                            variant="contained"
                                            onClick={() => onDelete(selectedEvent.id, workspaceId)}
                                            className="decline-button buttons-width-adjust"
                                        >
                                            <DeleteIcon style={{ marginRight: 4 }} />
                                            <span className='button-text'>
                                                {t("modal.delete")}
                                            </span>
                                        </Button>
                                    </div>
                                    <Box sx={{ flexGrow: 1 }} />
                                    <div className='right-side-buttons'> 
                                        <Button onClick={handleCancel} className="decline-button">
                                            <CancelIcon style={{ marginRight: 4 }} />
                                            <span className='button-text buttons-width-adjust'>
                                                {t("global.cancel")}
                                            </span>
                                        </Button>
                                        <Button
                                            variant="contained"
                                            onClick={handleSave}
                                            className="accept-button"
                                            disabled={isSubmitDisabled || (startYearValidationError || endYearValidationError)}
                                        >
                                            <CheckCircleIcon style={{ marginRight: 4 }} />
                                            <span className='button-text buttons-width-adjust'>
                                                {t("global.save_changes")}
                                            </span>
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                (myUserPerms?.is_admin || myUserPerms?.is_owner || myUserPerms?.user_id === selectedEvent?.creator) && (
                                    <Button
                                        variant="contained"
                                        onClick={handleEdit}
                                        className="button-light-bg"
                                    >
                                        {t("calendar.event_edit")}
                                    </Button>
                                )
                            )}
                        </Box>

                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    );
};

export default EventDetailsModal;
